import React, { useState, useEffect, useRef } from 'react';
import {
  FiMessageCircle, FiSend, FiRefreshCw, FiSearch, FiUser, FiPhone,
  FiClock, FiCheckCircle, FiXCircle, FiAlertCircle, FiTrendingUp,
  FiChevronLeft, FiChevronRight, FiMessageSquare,
} from 'react-icons/fi';
import { whatsappApi, WhatsAppConversation, WhatsAppMessage, ConversationsResponse, ConversationMessagesResponse, WhatsAppStats } from '../services/whatsappApi';
import CannedResponsePicker from './CannedResponsePicker';
import { appendCannedResponseContent, useCannedResponseShortcuts } from '../hooks/useCannedResponseShortcuts';
import './WhatsAppManagement.css';

const Icon = ({ icon: IconComponent, className }: { icon: any; className?: string }) =>
  React.createElement(IconComponent as any, { className });

const WhatsAppManagement: React.FC = () => {
  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string | null>(null);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendToPhone, setSendToPhone] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [showCannedReplyPicker, setShowCannedReplyPicker] = useState(false);
  const [showCannedSendPicker, setShowCannedSendPicker] = useState(false);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const sendTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [stats, setStats] = useState<WhatsAppStats | null>(null);

  useCannedResponseShortcuts(replyMessage, setReplyMessage);
  useCannedResponseShortcuts(sendMessage, setSendMessage);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    loadConversations();
    loadStats();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadConversations();
      loadStats();
      if (selectedPhoneNumber) {
        loadMessages(selectedPhoneNumber);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [currentPage]);

  useEffect(() => {
    if (selectedPhoneNumber) {
      loadMessages(selectedPhoneNumber);
    }
  }, [selectedPhoneNumber]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      const messagesList = document.querySelector('.wm-messages-list');
      if (messagesList) {
        setTimeout(() => {
          messagesList.scrollTop = messagesList.scrollHeight;
        }, 100);
      }
    }
  }, [messages]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const data: ConversationsResponse = await whatsappApi.getConversations(currentPage, pageSize);
      setConversations(data?.content || []);
      setTotalPages(data?.totalPages || 0);
      setUnreadCount(data?.unreadCount || 0);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setConversations([]);
      setTotalPages(0);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (phoneNumber: string) => {
    if (!phoneNumber) {
      setMessages([]);
      return;
    }
    
    try {
      setLoadingMessages(true);
      console.log('Loading messages for:', phoneNumber);
      const data: ConversationMessagesResponse = await whatsappApi.getConversation(phoneNumber);
      console.log('Messages response:', data);
      
      let messagesArray: WhatsAppMessage[] = [];
      if (data && data.messages) {
        messagesArray = Array.isArray(data.messages) ? data.messages : [];
      } else if (data && Array.isArray(data)) {
        // Handle case where API returns array directly
        messagesArray = data;
      }
      
      // Sort messages by timestamp (oldest first) so newest appear at bottom
      messagesArray.sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeA - timeB;
      });
      
      setMessages(messagesArray);
      
      // Refresh conversations to update read status
      loadConversations();
    } catch (error: any) {
      console.error('Failed to load messages:', error);
      console.error('Error details:', error.response?.data);
      setMessages([]);
      alert(`Failed to load messages: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    } finally {
      setLoadingMessages(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await whatsappApi.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleInsertCannedReply = (content: string) => {
    setReplyMessage(prev => appendCannedResponseContent(prev, content));
    setTimeout(() => replyTextareaRef.current?.focus(), 100);
  };

  const handleInsertCannedSend = (content: string) => {
    setSendMessage(prev => appendCannedResponseContent(prev, content));
    setTimeout(() => sendTextareaRef.current?.focus(), 100);
  };

  const handleSendReply = async () => {
    if (!selectedPhoneNumber || !replyMessage.trim()) return;

    try {
      setSending(true);
      const result = await whatsappApi.sendMessage({
        phoneNumber: selectedPhoneNumber,
        message: replyMessage,
      });
      
      if (result.success) {
        setReplyMessage('');
        // Reload messages to show the sent message
        await loadMessages(selectedPhoneNumber);
        loadConversations();
        loadStats();
      } else {
        alert(`Error: ${result.message || 'Failed to send message'}`);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to send message';
      alert(`Error: ${errorMessage}`);
    } finally {
      setSending(false);
    }
  };

  const handleSendNewMessage = async () => {
    if (!sendMessage.trim() || !sendToPhone.trim()) {
      alert('Please enter both phone number and message');
      return;
    }

    try {
      setSending(true);
      const result = await whatsappApi.sendMessage({
        phoneNumber: sendToPhone.trim(),
        message: sendMessage,
      });
      
      if (result.success) {
        setSendToPhone('');
        setSendMessage('');
        setShowSendModal(false);
        loadConversations();
        loadStats();
        alert('Message sent successfully!');
      } else {
        alert(`Error: ${result.message || 'Failed to send message'}`);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to send message';
      alert(`Error: ${errorMessage}`);
    } finally {
      setSending(false);
    }
  };

  const handleSelectConversation = (phoneNumber: string) => {
    setSelectedPhoneNumber(phoneNumber);
  };

  // Group conversations by phone number to show latest message
  // API returns individual messages, so we group them by phone number
  const groupedConversations = (conversations || []).reduce((acc, conv) => {
    if (!conv || !conv.phoneNumber) return acc;
    const existing = acc.find(c => c && c.phoneNumber === conv.phoneNumber);
    if (!existing) {
      acc.push(conv);
    } else {
      // Update if this message is newer
      if (conv.timestamp && existing.timestamp && new Date(conv.timestamp) > new Date(existing.timestamp)) {
        const index = acc.indexOf(existing);
        acc[index] = conv;
      }
    }
    return acc;
  }, [] as WhatsAppConversation[]);

  const filteredConversations = (groupedConversations || []).filter(conv => {
    if (!conv || !conv.phoneNumber) return false;
    const phoneMatch = conv.phoneNumber.includes(searchTerm);
    const nameMatch = conv.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    return phoneMatch || nameMatch;
  });

  // Sort by timestamp (newest first)
  (filteredConversations || []).sort((a, b) => {
    if (!a || !b) return 0;
    const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return timeB - timeA;
  });

  const selectedConversation = (filteredConversations || []).find(c => c && c.phoneNumber === selectedPhoneNumber);

  return (
    <div className="whatsapp-management">
      <div className="wm-header">
        <div className="wm-header-left">
          <Icon icon={FiMessageCircle} className="wm-header-icon" />
          <div>
            <h2>WhatsApp Messages</h2>
            <p>Manage and respond to WhatsApp conversations</p>
          </div>
        </div>
        <div className="wm-header-stats">
          {stats && (
            <>
              <div className="wm-stat-item">
                <Icon icon={FiTrendingUp} />
                <div>
                  <span className="wm-stat-value">{stats.totalConversations}</span>
                  <span className="wm-stat-label">Conversations</span>
                </div>
              </div>
              {unreadCount > 0 && (
                <div className="wm-stat-item wm-stat-unread">
                  <Icon icon={FiAlertCircle} />
                  <div>
                    <span className="wm-stat-value">{unreadCount}</span>
                    <span className="wm-stat-label">Unread</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="wm-header-actions">
          <button className="btn-primary" onClick={() => setShowSendModal(true)}>
            <Icon icon={FiSend} /> New Message
          </button>
          <button className="btn-refresh" onClick={() => { loadConversations(); loadStats(); }} disabled={loading}>
            <Icon icon={FiRefreshCw} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      <div className="wm-content">
        {/* Conversations List */}
        <div className="wm-conversations-panel">
          <div className="wm-search-box">
            <Icon icon={FiSearch} />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="wm-conversations-list">
            {loading ? (
              <div className="loading-state">
                <Icon icon={FiRefreshCw} className="spinning" />
                <p>Loading conversations...</p>
              </div>
            ) : !filteredConversations || filteredConversations.length === 0 ? (
              <div className="empty-state">
                <Icon icon={FiMessageCircle} />
                <p>No conversations found</p>
                <p className="empty-hint">WhatsApp messages will appear here when users send messages</p>
              </div>
            ) : (
              <>
                {(filteredConversations || []).map(conv => {
                  if (!conv) return null;
                  const unreadForThis = (conversations || []).filter(
                    c => c && c.phoneNumber === conv.phoneNumber && !c.isRead
                  ).length;
                  
                  const messageContent = conv.messageContent || '';
                  const timestamp = conv.timestamp || new Date().toISOString();
                  
                  return (
                    <div
                      key={conv.phoneNumber || Math.random()}
                      className={`wm-conversation-item ${selectedPhoneNumber === conv.phoneNumber ? 'active' : ''} ${!conv.isRead ? 'unread' : ''}`}
                      onClick={() => handleSelectConversation(conv.phoneNumber)}
                    >
                      <div className="wm-conv-avatar">
                        <Icon icon={FiUser} />
                      </div>
                      <div className="wm-conv-details">
                        <div className="wm-conv-header">
                          <span className="wm-conv-name">{conv.user?.name || conv.phoneNumber || 'Unknown'}</span>
                          {unreadForThis > 0 && (
                            <span className="wm-unread-badge">{unreadForThis}</span>
                          )}
                        </div>
                        <div className="wm-conv-preview">
                          <span className="wm-conv-message">
                            {conv.direction === 'INCOMING' ? '📥 ' : '📤 '}
                            {messageContent.length > 50 
                              ? messageContent.substring(0, 50) + '...' 
                              : messageContent}
                          </span>
                          <span className="wm-conv-time">
                            {new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="wm-pagination">
              <button
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                <Icon icon={FiChevronLeft} />
              </button>
              <span>Page {currentPage + 1} of {totalPages}</span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                <Icon icon={FiChevronRight} />
              </button>
            </div>
          )}
        </div>

        {/* Messages Panel */}
        <div className="wm-messages-panel">
          {selectedPhoneNumber ? (
            <>
              <div className="wm-messages-header">
                <div className="wm-messages-header-left">
                  <Icon icon={FiUser} />
                  <div>
                    <h3>{selectedConversation?.user?.name || selectedPhoneNumber}</h3>
                    <p>{selectedPhoneNumber}</p>
                  </div>
                </div>
                <button 
                  className="wm-refresh-btn" 
                  onClick={() => loadMessages(selectedPhoneNumber)}
                  title="Refresh messages"
                  disabled={loadingMessages}
                >
                  <Icon icon={FiRefreshCw} className={loadingMessages ? 'spinning' : ''} />
                </button>
              </div>

              <div className="wm-messages-list">
                {loadingMessages ? (
                  <div className="loading-state">
                    <Icon icon={FiRefreshCw} className="spinning" />
                    <p>Loading messages...</p>
                  </div>
                ) : !messages || messages.length === 0 ? (
                  <div className="empty-state">
                    <Icon icon={FiMessageCircle} />
                    <p>No messages yet</p>
                    <p className="empty-hint">Start a conversation by sending a message</p>
                  </div>
                ) : (
                  messages.map(msg => {
                    if (!msg) return null;
                    const isIncoming = msg.direction === 'INCOMING';
                    return (
                      <div
                        key={msg.id || Math.random()}
                        className={`wm-message ${isIncoming ? 'inbound' : 'outbound'}`}
                      >
                        <div className="wm-message-content">
                          {isIncoming && (
                            <div className="wm-message-sender">
                              <Icon icon={FiUser} className="wm-sender-icon" />
                              <span className="wm-sender-name">
                                {msg.user?.name || selectedPhoneNumber || 'Customer'}
                              </span>
                            </div>
                          )}
                          <p>{msg.messageContent || ''}</p>
                          <div className="wm-message-meta">
                            <span className="wm-message-time">
                              {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                            {!isIncoming && (
                              <Icon
                                icon={FiCheckCircle}
                                className={`wm-status-icon ${msg.isRead ? 'read' : 'sent'}`}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="wm-reply-box">
                <button
                  type="button"
                  className="wm-canned-btn"
                  onClick={() => setShowCannedReplyPicker(true)}
                  title="Insert canned response (type /shortcut + space)"
                >
                  <Icon icon={FiMessageSquare} />
                  Canned
                </button>
                <div className="wm-reply-row">
                  <textarea
                    ref={replyTextareaRef}
                    placeholder="Type /shortcut + space or your reply..."
                    value={replyMessage}
                    onChange={e => setReplyMessage(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendReply();
                      }
                    }}
                    rows={3}
                  />
                  <button
                    className="btn-send"
                    onClick={handleSendReply}
                    disabled={sending || !replyMessage.trim()}
                  >
                    <Icon icon={FiSend} />
                    {sending ? 'Sending...' : 'Send'}
                  </button>
                </div>
                <CannedResponsePicker
                  anchorEl={replyTextareaRef.current}
                  open={showCannedReplyPicker}
                  onClose={() => setShowCannedReplyPicker(false)}
                  onSelect={handleInsertCannedReply}
                  placement="top"
                />
              </div>
            </>
          ) : (
            <div className="wm-no-selection">
              <Icon icon={FiMessageCircle} />
              <p>Select a conversation to view messages</p>
            </div>
          )}
        </div>
      </div>

      {/* Send New Message Modal */}
      {showSendModal && (
        <div className="modal-overlay" onClick={() => setShowSendModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send New WhatsApp Message</h3>
              <button className="btn-close" onClick={() => setShowSendModal(false)}>
                <Icon icon={FiXCircle} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="text"
                  placeholder="+966540329839"
                  value={sendToPhone}
                  onChange={e => setSendToPhone(e.target.value)}
                />
                <span className="field-hint">Format: +country code + number (e.g., +966540329839)</span>
              </div>
              <div className="form-group">
                <div className="wm-modal-message-header">
                  <label>Message *</label>
                  <button
                    type="button"
                    className="wm-canned-btn wm-canned-btn--compact"
                    onClick={() => setShowCannedSendPicker(true)}
                    title="Insert canned response"
                  >
                    <Icon icon={FiMessageSquare} />
                    Canned
                  </button>
                </div>
                <textarea
                  ref={sendTextareaRef}
                  value={sendMessage}
                  onChange={e => setSendMessage(e.target.value)}
                  rows={5}
                  placeholder="Type /shortcut + space or your message..."
                />
                <CannedResponsePicker
                  anchorEl={sendTextareaRef.current}
                  open={showCannedSendPicker}
                  onClose={() => setShowCannedSendPicker(false)}
                  onSelect={handleInsertCannedSend}
                  placement="bottom"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowSendModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSendNewMessage} disabled={sending || !sendMessage.trim() || !sendToPhone.trim()}>
                <Icon icon={FiSend} />
                {sending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppManagement;
